import React, { useCallback, useContext, useEffect, useRef } from "react";
import PropTypes from "prop-types";
import qapi from "views_shared/app/javascript/api";
import { expertUrl } from "views_shared/app/javascript/utils/urls";
import ProfilePage from "../profiles/ProfilePage";
import useScrollToHashSection from "../profiles/use_scroll_to_hash_section";
import ViewersAlsoView, { useViewersAlsoView } from "../profiles/ViewersAlsoView";
import ChecklistCard from "../profiles/ChecklistCard";
import useProfileCompletion from "../profiles/ChecklistCard/use_profile_completion";
import ConnectSection, { connectTitle, firstName } from "../profiles/sections/ConnectSection";
import useTeamMembers from "../pr_profiles/data/use_team_members";
import TeamMembersCard from "../pr_profiles/show/team_members_card";
import { ExpertContext } from "./data/expert_context_management";
import ExpertPersonHeader from "./show/expert_person_header";
import ExpertSections, { EXPERT_ALSO_REPRESENTS_SECTION_ID } from "./show/expert_sections";
import ExpertSkeleton from "./show/expert_skeleton";
import ExpertEditModal from "./edit/edit_modal";
import RepresentedByCard from "./show/represented_by_card";

// A hybrid's "Represents" link uses the PR section hash; the pr_profiles URL redirects hybrids here and
// carries it along. Treat it as an alias for this page's Also-represents section so both that redirect
// and any direct/bookmarked `#pr-section-represents` visit still scroll.
const REPRESENTS_HASH_ALIASES = { "pr-section-represents": EXPERT_ALSO_REPRESENTS_SECTION_ID };

// The Checklist card's factor list for an Expert/Hybrid profile, in the design's row order — the
// seven boolean columns id_expert_completion_scores_view exposes (Hybrid reuses this
// same view via its self-representing expert entity, so no separate factor set is needed).
const EXPERT_CHECKLIST_FACTORS = [
  { key: "has_profile_image", label: "Add a headshot" },
  { key: "has_description", label: "Add a bio" },
  { key: "has_headline", label: "Add a headline" },
  { key: "has_jobs_or_products", label: "Add employment or products" },
  { key: "has_contact_details", label: "Add contact info" },
  { key: "has_quotes", label: "Add a quote" },
  { key: "has_representatives", label: "Get represented by a PR professional" },
];

// Hydrate adapter for the Viewers-also-view rail: loads display rows for the similar-expert ids
// through the experts view, whose RLS drops any profile this viewer can't see.
const hydrateSimilarExperts = async (ids) => {
  const response = await qapi.experts.getAllById(ids);
  const rows = Array.isArray(response.data) ? response.data : [];
  return rows.map((row) => ({
    id: row.id,
    href: expertUrl({ slug: row.slug, migrate_id: row.migrate_id }),
    imageUrl: row.profile_image_url,
    title: row.name,
    subtitle: row.featured_title_at_company,
  }));
};

// Expert show (the inner component under ExpertTopLevelShow's provider): the shared <ProfilePage>
// chassis with a <PersonHeader> + section cards, plus a right rail of the Checklist, Connect,
// Represented by, Team Members and Viewers-also-view cards. Hybrid profiles render here too
// (hybrid-aware avatar + the Also-represents shell) until the dedicated hybrid page exists; the same
// rail wiring serves both (Hybrid's checklist needs no new SQL — it hangs off the same
// self-representing expert entity as its `expert_completion_scores` row).
function Expert({ expertId }) {
  const {
    setExpertId,
    isLoading,
    asyncError,
    expert,
    companyJobs,
    representedBy,
    selfProfileId,
    viewerContext,
    setOpenEditModal,
    requestHeadshotPicker,
    requestAddQuote,
  } = useContext(ExpertContext);
  const editable = !!viewerContext?.editable;

  // Deep-links checklist rows to the edit affordance that fixes them. Most rows open a
  // header/employment edit modal; the headshot and quote rows have no modal, so they open the header
  // avatar's file picker and the Quotes section's inline add editor respectively.
  // `has_representatives` ("Get represented by a PR professional") stays static: there is no edit
  // modal for soliciting outside representation.
  const checklistFactorActions = {
    has_profile_image: requestHeadshotPicker,
    has_description: () => setOpenEditModal("HEADER"),
    has_headline: () => setOpenEditModal("HEADER"),
    has_jobs_or_products: () => setOpenEditModal("EMPLOYMENT"),
    has_quotes: requestAddQuote,
  };

  // Owner/admin-only widget: idling the hook (rather than just gating the render) for a non-editable
  // viewer means it never even issues the read. A collapsed card still renders its header, so it
  // still counts as rail content; a hidden one doesn't.
  const checklist = useProfileCompletion({
    resourceClient: qapi.expertCompletionScores,
    id: editable ? expert?.id : null,
    kind: "expert",
    refreshKey: expert,
  });

  // Quotes save outside the expert reload, so re-read the score when they empty out or fill in.
  const { refresh: refreshChecklist } = checklist;
  const settledQuotes = useRef({ expertId: null, empty: null });
  const handleQuotesEmptyChange = useCallback(
    (empty) => {
      const settled = settledQuotes.current;
      if (settled.expertId === expert?.id && settled.empty !== null && settled.empty !== empty) refreshChecklist();
      settledQuotes.current = { expertId: expert?.id, empty };
    },
    [expert?.id, refreshChecklist],
  );
  const checklistContributes = editable && !checklist.hidden && (checklist.isLoading || !!checklist.score);

  // The similarity query needs the numeric expert id (the route param may be a slug), so it idles
  // until the expert row has loaded — which the page-level skeleton covers anyway.
  const viewersAlsoView = useViewersAlsoView({
    queryModelType: "Id::PromoEntity::Expert",
    queryModelId: expert?.id,
    hydrate: hydrateSimilarExperts,
  });

  // Team Members hangs off ONE employment, same pick as PR's: the first job that isn't ended and
  // isn't hidden from this expert's own profile. It lists the company's experts first, then its PR
  // people, leaving out the expert and — for a self-representing expert — their own PR profile.
  const teamEmployment = (companyJobs || []).find((job) => !job.to && !job.personHidden);
  const teamMembers = useTeamMembers({
    companyId: expert?.id ? teamEmployment?.companyId : null,
    excludeExpertId: expert?.id,
    excludePromoterId: selfProfileId,
    expertsFirst: true,
  });

  // The read-only Connect card holds only contact details; Message lives in the header.
  const contactDetails = expert?.contact_details || [];
  const hasConnectContent = contactDetails.length > 0;

  const reps = representedBy || [];

  useEffect(() => {
    setExpertId(expertId);
  }, [expertId, setExpertId]);

  // Deep-linked scroll for a hybrid's "Represents" link — either the current
  // `#expert-section-also-represents` hash or the legacy `#pr-section-represents` (see the alias).
  useScrollToHashSection(!isLoading, REPRESENTS_HASH_ALIASES);

  // Let the top-level ErrorBoundary handle async failures.
  if (asyncError) throw asyncError;

  // Rails found the expert but the API returned no row — the viewer isn't entitled to it (the
  // PostgREST view enforces visibility/RLS; the Rails route doesn't). Say so instead of
  // skeleton-ing forever.
  if (!isLoading && !expert) {
    return <div className="alert alert-secondary">This profile isn&apos;t available.</div>;
  }

  // ProfilePage sizes its columns on `rightRail` truthiness alone, so a fragment of self-collapsing
  // cards would leave a phantom rail: the page has to know every card's state. The rail stays while
  // any of them has content or is still loading, and goes away only once all settle empty — then the
  // main column takes the full width.
  const railHasContent =
    checklistContributes ||
    hasConnectContent ||
    reps.length > 0 ||
    teamMembers.isLoading ||
    teamMembers.members.length > 0 ||
    viewersAlsoView.isLoading ||
    viewersAlsoView.items.length > 0;

  return (
    <div>
      <ExpertEditModal />

      <ProfilePage
        isLoading={isLoading}
        skeleton={<ExpertSkeleton />}
        header={<ExpertPersonHeader />}
        rightRail={
          railHasContent ? (
            <>
              {!checklist.hidden && (
                <ChecklistCard
                  score={checklist.score}
                  factors={EXPERT_CHECKLIST_FACTORS}
                  isLoading={checklist.isLoading}
                  factorActions={checklistFactorActions}
                  onHide={checklist.hide}
                />
              )}
              {hasConnectContent && (
                <ConnectSection
                  id="expert-connect"
                  title={connectTitle(firstName(expert?.name))}
                  contactDetails={contactDetails}
                />
              )}
              <RepresentedByCard representedBy={reps} />
              <TeamMembersCard
                members={teamMembers.members}
                totalCount={teamMembers.totalCount}
                isLoading={teamMembers.isLoading}
              />
              <ViewersAlsoView items={viewersAlsoView.items} isLoading={viewersAlsoView.isLoading} />
            </>
          ) : null
        }
      >
        {/* Keying on the resolved owner (never the route prop) makes an owner swap remount the whole
            section tree, discarding any settled-empty flags and in-flight reads the old owner's rows
            left behind, rather than tracking that staleness by hand. `expert` starts null while the
            provider loads, so the key can't be evaluated yet — an empty fragment (not null) keeps
            ProfilePage's required `children` prop satisfied during that render. */}
        {/* eslint-disable-next-line react/jsx-no-useless-fragment -- an empty fragment is the point:
            it satisfies ProfilePage's required `children` node without rendering anything, avoiding the
            prop-type warning a bare `null` would raise during this loading render. */}
        {expert ? <ExpertSections key={expert.id} onQuotesEmptyChange={handleQuotesEmptyChange} /> : <></>}
      </ProfilePage>
    </div>
  );
}

Expert.propTypes = {
  expertId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
};

export default Expert;
